import { Moment } from "moment";
import {
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IAttemptTrayConfig,
  ICalendarSet,
  ICalendarWorkTemplateBase,
  ITenantHolidayResponse,
  IUserListItemResponse,
} from "@miplatform/types";
import { Theme } from "@mui/material";

export enum PrioritySchedulerHeaderActionType {
  View,
  ViewMore,
  ViewProfile,
  AddFromFollowUp,
  Scheduling,
  MarkInactive,
  RemoveInactive,
  ClearSchedule,
}

export interface IPrioritySchedulerAction {
  label: string;
  icon: string;
  iconColor?: (theme: Theme) => string;
  type: PrioritySchedulerHeaderActionType;
  getIsActionHidden?: (user: IUserListItemResponse) => boolean;
  actions?: IPrioritySchedulerAction[];
}

export interface IPrioritySchedulerProps {
  calendarSet: ICalendarSet | null;
  tenantHolidays: ITenantHolidayResponse[];
  columns: IPrioritySchedulerColumnData[];
  columnWidth: number;
  hourHeight: number;
  date: Moment;
  zoom?: number;
  headerActions?: IPrioritySchedulerAction[];
  onHeaderActionClick?: (
    actionType: PrioritySchedulerHeaderActionType,
    user?: IUserListItemResponse
  ) => void;
  onEventRemove?: (entityId: string) => void;
  trays?: IAttemptTrayConfig[];
  onCardClick?: (attempt: IAttempt) => void;
  EventComponent?: React.ComponentType<PrioritySchedulerEventViewComponentProps>;
  onCardMove?: (move: PrioritySchedulerMovePayload) => void;
  afterCardDrop?: (payload: PrioritySchedulerAfterCardDropPayload) => void;
  onViewMore?: (user: IUserListItemResponse) => void;
}

export interface IDisabledSlot {
  id: string;
  start: Moment;
  end: Moment;
  note: string;
}

export interface PrioritySchedulerEventViewComponentProps {
  attempt: IAttempt;
  onEventRemove?: () => void;
  onCardClick?: (attempt: IAttempt) => void;
  loading?: boolean;
  isRemovable?: boolean;
  className?: string;
}

export interface IPrioritySchedulerColumnHeaderProps {
  user?: IUserListItemResponse;
  eventCount?: number;
  holdCount?: number;
  date?: Moment;
  dateView?: boolean;
  trays?: IAttemptTrayConfig[];
  actions: IPrioritySchedulerAction[];
  onActionClick?: (
    actionType: PrioritySchedulerHeaderActionType,
    user?: IUserListItemResponse
  ) => void;
}

export type PrioritySchedulerColumnEvent =
  IAttemptListItemResponseForWorkOrder & {
    isGhost?: boolean;
    isStaged?: boolean;
  };

export interface IPrioritySchedulerColumnData {
  id: string;
  date?: Moment;
  user?: IUserListItemResponse;
  dateView?: boolean;
  events?: PrioritySchedulerColumnEvent[];
  disabledSlots?: IDisabledSlot[];
}
export interface IPrioritySchedulerColumnProps
  extends IPrioritySchedulerColumnData {
  EventComponent: React.ComponentType<PrioritySchedulerEventViewComponentProps>;
  dayStartTime?: string;
  dayEndTime?: string;
  hourHeight: number;
  holiday?: ITenantHolidayResponse;
  /** Total timeline height in px (e.g. full day grid); used to size the drop zone. */
  timelineContentHeight: number;
  /** When `date` is missing (e.g. per-user columns), anchor times to this calendar day. */
  layoutReferenceDate?: Moment;
  onEventRemove?: (entityId: string) => void;
  onCardClick: (attempt: IAttempt) => void;
  columnDnDId?: string;
  isDropTarget?: boolean;
  overCardId?: string;
  /** Snapped Y offset (px) for the drag indicator line relative to the card container top. */
  dragIndicatorY?: number;
  workTemplateBases?: ICalendarWorkTemplateBase[];
  registerColumnDroppableNode?: (
    columnId: string,
    node: HTMLElement | null
  ) => void;
}

export interface IPrioritySchedulerBucketsProps {
  trays?: IAttemptTrayConfig[];
  onCardClick: (attempt: IAttempt) => void;
  EventComponent: React.ComponentType<PrioritySchedulerEventViewComponentProps>;
  indicatorWidth: number;
}

export type PrioritySchedulerContainerType = "column" | "tray";

export interface PrioritySchedulerLocation {
  type: PrioritySchedulerContainerType;
  id: string;
}

export interface PrioritySchedulerMovePayload {
  cardId: string;
  from: PrioritySchedulerLocation;
  to: PrioritySchedulerLocation;
  overCardId?: string;
  dropOffsetY?: number;
}

export type PrioritySchedulerDropPosition = "before" | "end" | "absolute";

export interface PrioritySchedulerAffectedCard {
  attemptId: string;
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  userId?: string;
}

export interface PrioritySchedulerAfterCardDropPayload
  extends PrioritySchedulerMovePayload {
  dropPosition: PrioritySchedulerDropPosition;
  affectedCards: PrioritySchedulerAffectedCard[];
  columns: IPrioritySchedulerColumnData[];
  /** Other attempts in the destination column whose scheduled interval intersects the dropped card. */
  overlappingAttemptIds?: string[];
}

export interface PrioritySchedulerCardDragData {
  type: "card";
  cardId: string;
  container: PrioritySchedulerLocation;
}

export interface PrioritySchedulerContainerDragData {
  type: "container";
  container: PrioritySchedulerLocation;
  onCardClick?: (attempt: IAttempt) => void;
}
